import {
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BasketItemType, BasketService, QuoteRequest } from '@basket';
import { CatalogueService } from '@catalogue';
import {
  DocumentPathService,
  JourneyStepTrackerService,
  JourneyType,
} from '@common';
import {
  BadgeTextComponent,
  BenefitsListItem,
  ButtonComponent,
  CardComponent,
  GridColumnComponent,
  GridContainerComponent,
  GridRowComponent,
  IconComponent,
  LinkComponent,
  PanelComponent,
  PartnerLogoGridComponent,
  ProgressionStepsComponent,
  TrustboxComponent,
} from '@components';
import { CONFIG } from '@config';
import { CustomerService } from '@customer';
import { GADataService } from '@data-access';
import { ErrorRoutes } from '@features/error';
import { LabelledCheckboxComponent } from '@forms';
import { extractPageNameFromUrl, GAEvent, GAJourneyVersion, GATag } from '@ga';
import {
  CurrencyFormatPipe,
  LocaleId,
  provideTranslationDictionaryMapping,
  TranslatePipe,
  TranslateService,
} from '@i18n';
import { PosthogTrackingEvents, TrackingService } from '@tracking';
import posthog from 'posthog-js';
import { catchError, tap } from 'rxjs';
import { BenefitComponent } from '../../components/benefit/benefit.component';
import { FeatureMultiPlanTranslationDictionary } from '../../i18n/feature-multi-plan-dictionary.interface';
import { translation } from '../../i18n/translation.constant';
import { MultiPlanFeatureFlag } from '../../models/multi-plan-feature-flags.enum';
import { MultiPlanProtectionDetailsVariants } from '../../models/multi-plan-protection-details-variants.enum';
import { MultiPlanService } from '../../services/multi-plan/multi-plan.service';

@Component({
  selector: 'app-plan-sale',
  imports: [
    BadgeTextComponent,
    BenefitComponent,
    ButtonComponent,
    CardComponent,
    CurrencyFormatPipe,
    GridColumnComponent,
    GridContainerComponent,
    GridRowComponent,
    IconComponent,
    LabelledCheckboxComponent,
    LinkComponent,
    PanelComponent,
    PartnerLogoGridComponent,
    ProgressionStepsComponent,
    TranslatePipe,
    TrustboxComponent,
  ],
  templateUrl: './plan-sale.component.html',
  styleUrl: './plan-sale.component.scss',
  providers: [provideTranslationDictionaryMapping(translation)],
})
export class PlanSaleComponent implements OnInit {
  private readonly basketService = inject(BasketService);
  private readonly catalogueService = inject(CatalogueService);
  private readonly config = inject(CONFIG);
  private readonly customerService = inject(CustomerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly documentPathService = inject(DocumentPathService);
  private readonly trackingService = inject(TrackingService);
  private readonly gaDataService = inject(GADataService);
  private readonly journeyStepTracker = inject(JourneyStepTrackerService);
  private readonly localeId = inject<LocaleId>(LOCALE_ID);
  private readonly multiPlanService = inject(MultiPlanService);
  private readonly router = inject(Router);
  private readonly translateService = inject(
    TranslateService<FeatureMultiPlanTranslationDictionary>,
  );

  readonly appliances = this.multiPlanService.appliances;
  readonly buildYourBundleOpened = this.multiPlanService.buildYourBundleOpened;
  readonly showSelectedAppliancesDisclaimer =
    this.multiPlanService.showSelectedAppliancesDisclaimer;

  private readonly clientBrandCode = this.config.clientBrandCode;
  readonly clientName = this.config.clientName;
  readonly initialAppliance = this.basketService.initialRegisteredAppliance;
  readonly initialApplianceBrand =
    this.multiPlanService.initialApplianceBrand();
  readonly selectedAppliances = this.multiPlanService.selectedAppliances;
  readonly total = this.multiPlanService.total;
  readonly additionalAppliancePrice =
    this.multiPlanService.additionalAppliancePrice;
  readonly priceStartingFrom = this.multiPlanService.priceStartingFrom;
  readonly vm = this.basketService.vm();
  readonly lowRiskPurchasePrice = this.catalogueService.lowRiskPurchasePrice();

  readonly localeIdIsGB = computed(
    () => this.localeId === LocaleId.English_GreatBritain,
  );

  readonly subsequentQuotePayload = computed<QuoteRequest | undefined>(() => {
    const appliance = this.catalogueService.steppedFormAppliance();

    const applianceWarranty =
      this.catalogueService.steppedFormApplianceWarranty();
    const price = this.catalogueService.selectedAppliancePurchasePrice();
    const date = this.catalogueService.selectedAppliancePurchaseDate();
    const modelNumber = this.catalogueService.selectedApplianceModelNumber();
    const serialNumber = this.catalogueService.selectedApplianceSerialNumber();
    const formattedDate = {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };

    if (!appliance) {
      return undefined;
    }
    return {
      applianceCode: appliance.applianceCode,
      brandCode: this.clientBrandCode,
      itemType: BasketItemType.Quote,
      ...(modelNumber && { modelNumber }),
      ...(serialNumber && { serialNumber }),
      purchaseDay: formattedDate.day,
      purchaseMonth: formattedDate.month,
      purchaseYear: formattedDate.year,
      purchasePrice: price,
      ...(applianceWarranty && { warranty: applianceWarranty }),
      isMultiPlanQuote: true,
      noOfAdditionalAppliances: this.selectedAppliances().length,
    };
  });

  inclusionExpanded = true;
  formSubmitted = false;

  readonly multiPlanFeatureFlag: typeof MultiPlanFeatureFlag =
    MultiPlanFeatureFlag;

  readonly multiPlanProtectionDetailsVariants: typeof MultiPlanProtectionDetailsVariants =
    MultiPlanProtectionDetailsVariants;

  readonly sharedPartialGATag: GATag = {
    multiPlanOffer: this.gaDataService.multiPlanOffer(),
    offerStatus: this.gaDataService.offerStatus(),
    offerPrice: this.multiPlanService.singleAppliancePrice(),
    ...this.gaDataService.preQuoteAppliance(),
    event: GAEvent.GenericEvent,
  };

  ngOnInit(): void {
    posthog.capture(PosthogTrackingEvents.MultiPlanSalePageLoaded);
    posthog.capture(PosthogTrackingEvents.MultiPlanWasOffered);
    this.customerService.setCustomerSelectedToPurchasePlan(false);
    this.customerService.setCustomerSelectedToRegisterOnly(false);
    this.multiPlanService.resetSelectedAppliancesDetails();

    this.journeyStepTracker.updateStep(
      2,
      3,
      this.translateService.translate('STEP_TITLE_EXTRA_COVER_OPTIONAL'),
      false,
    );

    if (this.basketService.basketContainsBundleAppliances()) {
      this.basketService
        .removeAppliancesFromBasket()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }

    this.documentPathService.setJourneyType(JourneyType.MultiPlan);
  }

  onApplianceSelectedChange(appliance: string, isSelected: boolean) {
    const currentAppliances = this.appliances();
    const updatedAppliances = currentAppliances.map((a) =>
      a.applianceName === appliance ? { ...a, isSelected } : a,
    );
    this.appliances.set(updatedAppliances);
    posthog.capture(
      PosthogTrackingEvents.MultiPlanProtectionPageMultiApplianceSelector,
      {
        appliance,
        isSelected,
      },
    );
  }

  resetSelectedBundleAppliances() {
    this.appliances.set(
      this.appliances().map((a) => ({ ...a, isSelected: false })),
    );
  }

  multiAppliancePlanClicked(): void {
    this.formSubmitted = true;
    posthog.capture(
      PosthogTrackingEvents.MultiPlanProtectionPageMultiApplianceSelectorContinue,
      { appliance_count: this.selectedAppliances().length + 1 },
    );
    this.customerService.setCustomerSelectedToPurchasePlan(true);

    this.basketService
      .getSubsequentV1Quote(this.subsequentQuotePayload())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.router.navigate(['multi-plan', 'appliance-details'])),
        catchError(() => this.router.navigate([ErrorRoutes.Error])),
      )
      .subscribe();
  }

  singleAppliancePlanClicked(): void {
    this.customerService.setCustomerSelectedToPurchasePlan(true);
    this.basketService.setBasket(this.basketService.initialBasket());
    this.resetSelectedBundleAppliances();

    this.gaDataService.setJourneyVersion(GAJourneyVersion.SingleMultiPlan);

    this.trackingService.trackEvent({
      ...this.sharedPartialGATag,
      eventAction: 'enhance',
      eventCategory: extractPageNameFromUrl(),
      eventLabel: 'enhance',
      journeyType: this.gaDataService.journeyVersion(),
    });

    posthog.capture(
      PosthogTrackingEvents.MultiPlanWasOfferedSinglePlanSelected,
    );

    this.router.navigate(['multi-plan', 'review-cover']);
  }

  registerOnlyClicked(): void {
    this.customerService.setCustomerSelectedToPurchasePlan(false);
    this.customerService.setCustomerSelectedToRegisterOnly(true);
    this.gaDataService.setJourneyVersion(GAJourneyVersion.RegisterOnly);

    this.trackingService.trackEvent({
      ...this.sharedPartialGATag,
      eventAction: 'enhance',
      eventCategory: extractPageNameFromUrl(),
      eventLabel: 'No thanks, register only',
      journeyType: this.gaDataService.journeyVersion(),
    });

    posthog.capture(PosthogTrackingEvents.MultiPlanWasOfferedRegOnlySelected);

    this.router.navigate(['contact-details']);
  }

  buildYourBundleClicked(): void {
    this.buildYourBundleOpened.set(true);
    this.gaDataService.setJourneyVersion(GAJourneyVersion.MultiPlan);
    posthog.capture(PosthogTrackingEvents.MultiPlanBuildYourBundleCtaSelected);
    this.trackingService.trackEvent({
      ...this.sharedPartialGATag,
      eventAction: 'enhance',
      eventCategory: extractPageNameFromUrl(),
      eventLabel: 'Build your bundle',
      journeyType: this.gaDataService.journeyVersion(),
    });
  }

  toggleInclusionExpanded(): void {
    this.inclusionExpanded = !this.inclusionExpanded;
  }

  get benefits(): BenefitsListItem[] {
    return this.translateService.translate('PLAN_SALE_PAGE_BENEFITS_LIST');
  }

  get benefitsAlt(): string[] {
    return this.translateService.translate('PLAN_SALE_PAGE_ALT_BENEFITS_LIST');
  }

  get singleAppliancePrice(): number {
    return this.multiPlanService.singleAppliancePrice();
  }

  get activeMultiPlanFeatureFlag(): string | boolean | undefined {
    return posthog.getFeatureFlag(
      MultiPlanFeatureFlag.MultiPlanProtectionDetails,
    );
  }
}
